import React, {useState, useEffect, useRef, useMemo, useCallback} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {FiX, FiPlay, FiCheck, FiStar, FiPhone, FiMail, FiUser, FiBriefcase, FiChevronDown, FiSearch, FiFilter} from 'react-icons/fi';
import './App.css';
import HeroSection from './components/HeroSection';
import WorkshopTimer from './components/WorkshopTimer';
import RecentRegistrations from './components/RecentRegistrations';
import {useWorkshopSchedule} from './hooks/useWorkshopSchedule';

// UPDATED: Webhook URL for lead capture
const WEBHOOK_URL = "https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjYwNTY0MDYzZjA0MzY1MjZiNTUzNjUxM2Ii_pc";

// UPDATED: Payment page URL (Updated to page 24468)
const PAYMENT_PAGE_BASE_URL = "https://paymentz.aifunnels.app/product/21362/page/24468/addon-page";

// --- Animation Variants ---
const containerVariants = {
  hidden: {opacity: 0},
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      ease: "easeInOut"
    }
  }
};

const itemVariants = {
  hidden: {opacity: 0, y: 20},
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 12
    }
  }
};

const sectionFadeIn = {
  initial: {opacity: 0, y: 30},
  whileInView: {opacity: 1, y: 0},
  transition: {duration: 0.7, ease: "easeInOut"},
  viewport: {once: true, amount: 0.2}
};

// Video testimonials data
const testimonialVideos = [
  {
    id: 1,
    url: "https://www.youtube.com/watch?v=v3EEtDIaC3w",
    embedId: "v3EEtDIaC3w",
    thumbnail: "https://img.youtube.com/vi/v3EEtDIaC3w/maxresdefault.jpg",
    title: "How Funnels Done Transformed My Coaching Business",
    name: "A Woman Entrepreneur Coach"
  },
  {
    id: 2,
    url: "https://youtu.be/IrfPwkcSTB0?si=4zR7J5O2aIbdr7k0",
    embedId: "IrfPwkcSTB0",
    thumbnail: "https://img.youtube.com/vi/IrfPwkcSTB0/maxresdefault.jpg",
    title: "From Struggling to 2 Lakhs: My Funnels Done Success Story",
    name: "Bridal Lead Generation Expert Coach"
  },
  {
    id: 3,
    url: "https://youtu.be/F26JiMS-w4I?si=5CoJ4w0AnUAMPYZm",
    embedId: "F26JiMS-w4I",
    thumbnail: "https://img.youtube.com/vi/F26JiMS-w4I/maxresdefault.jpg",
    title: "Overcoming Coaching Business Challenges with Funnels Done",
    name: "Coaching Business"
  },
  {
    id: 4,
    url: "https://youtu.be/yWzJt7jv5CM?si=cFmZMEoNQgvFxmh4",
    embedId: "yWzJt7jv5CM",
    thumbnail: "https://img.youtube.com/vi/yWzJt7jv5CM/maxresdefault.jpg",
    title: "How I Made 30 Lakhs in a Year by Helping a Lot of People as a Coach without Quiting my Job",
    name: "Professor"
  },
  {
    id: 5,
    url: "https://youtu.be/vRzrNK9XPmQ?si=HcsiRvsrjOQhzfiO",
    embedId: "vRzrNK9XPmQ",
    thumbnail: "https://img.youtube.com/vi/vRzrNK9XPmQ/maxresdefault.jpg",
    title: "Want More Customers? 🤔 See How Manivanan Did It with FunnelsDone",
    name: "Trading Coach"
  },
  {
    id: 6,
    url: "https://youtube.com/shorts/L0jJ2NHIQ8w?si=vDvB1EIcSDn0lzHX",
    embedId: "L0jJ2NHIQ8w",
    thumbnail: "https://img.youtube.com/vi/L0jJ2NHIQ8w/maxresdefault.jpg",
    title: "The Secret Behind Mariyappan's Business Growth 🔥 | FunnelsDone AI",
    name: "Iniya Jewelers Madurai"
  }
];

// Client Feedback data
const clientFeedbacks = [
  {
    id: 1,
    name: "A R Ahamed Junaith",
    role: "Spoken English Coach",
    image: "https://storage.files-vault.com/uploads/1760941910-ht57JBdJ4f.webp",
    quote: "21+ Lakhs Revenue in Just 10 Months — While Still Working as College Professor"
  },
  {
    id: 2,
    name: "Maria Sundar",
    role: "Stock Trader Coach",
    image: "https://storage.files-vault.com/uploads/1760942061-nsFEgCbsBd.webp",
    quote: "1,000+ Paid Members & 3.8 Lakhs Collected in Just 60 Days with Trading Webinars"
  },
  {
    id: 3,
    name: "Arjumand",
    role: "Community Growth Expert",
    image: "https://storage.files-vault.com/uploads/1760942064-I6HBtoRq5t.webp",
    quote: "60 Organic Registrations, 30 Showups, Multiple Sales in 1 Week — Zero Ads!"
  },
  {
    id: 4,
    name: "Subiya Fathima",
    role: "Bridal Leads Coach",
    image: "https://storage.files-vault.com/uploads/1760942062-cWbhjZc7n9.webp",
    quote: "Sold 5,999 Course to 19 Clients in One Day — Cleared Son's School Fee Same Week"
  },
  {
    id: 5,
    name: "Manigandan",
    role: "FiveStar",
    image: "https://storage.files-vault.com/uploads/1760942066-tiqyCho8Gf.webp",
    quote: "Tamil Solopreneurs Crossed 1 Crore in a Week, 10 Lakhs in a Month & More — Using AI Smart Funnels"
  },
  {
    id: 6,
    name: "Bindhu Madhavan",
    role: "Prompt Committee",
    image: null,
    quote: "I started with zero audience and no marketing budget. In just 60 days, I built a ₹3.8 Lakh income system with over 1,000 paid members"
  }
];

// FAQ data
const faqData = [
  {
    id: 1,
    question: "When and where is this workshop happening?",
    answer: "This is a 3-hour LIVE online workshop held **every Sunday from 10:00 AM to 1:00 PM IST** (India Standard Time). Once you register, you'll receive the secure joining link via email and WhatsApp."
  },
  {
    id: 2,
    question: "Is this session live or pre-recorded?",
    answer: "It's 100% LIVE. Our mentor will teach, explain the concepts step-by-step, and answer your specific questions in real-time."
  },
  {
    id: 3,
    question: "Do I need any technical knowledge or special tools?",
    answer: "No. This workshop is designed for beginners. No coding or advanced tech skills are needed. We will guide you through user-friendly tools and concepts in simple terms."
  },
  {
    id: 4,
    question: "Will I get recordings if I can't attend live?",
    answer: "To maintain the exclusivity and high-impact nature of the live session, recordings will not be provided. We encourage you to attend live to get the full value."
  },
  {
    id: 5,
    question: "How do I know this will actually help me?",
    answer: "This workshop is built for coaches, consultants, and business owners who are non-technical. We provide step-by-step guidance, free tools, and ongoing support so you never feel lost."
  },
  {
    id: 6,
    question: "What happens after I register?",
    answer: "You'll immediately receive a confirmation email and WhatsApp message with your unique joining link, bonus details, and instructions. You'll also get helpful reminders so you don't miss the live session."
  },
  {
    id: 7,
    question: "What if I have more questions or get stuck later?",
    answer: "Upon joining, you get Lifetime Access to our exclusive private community, which includes courses, templates, and live support. You're never alone on this journey."
  },
  {
    id: 8,
    question: "Still have a doubt?",
    answer: "We're here to help! Just WhatsApp us or email us at support@yinguru.com. Our team is ready to help you get started with confidence."
  }
];

// Expanded Country Codes List
const countryCodes = [
  {code: "+91", country: "IN", name: "India", flag: "🇮🇳"},
  {code: "+1", country: "US", name: "USA", flag: "🇺🇸"},
  {code: "+60", country: "MY", name: "Malaysia", flag: "🇲🇾"},
  {code: "+65", country: "SG", name: "Singapore", flag: "🇸🇬"},
  {code: "+44", country: "GB", name: "UK", flag: "🇬🇧"},
  {code: "+971", country: "AE", name: "UAE", flag: "🇦🇪"},
  {code: "+61", country: "AU", name: "Australia", flag: "🇦🇺"},
  {code: "+1", country: "CA", name: "Canada", flag: "🇨🇦"},
  {code: "+54", country: "AR", name: "Argentina", flag: "🇦🇷"},
  {code: "+591", country: "BO", name: "Bolivia", flag: "🇧🇴"},
  {code: "+55", country: "BR", name: "Brazil", flag: "🇧🇷"},
  {code: "+56", country: "CL", name: "Chile", flag: "🇨🇱"},
  {code: "+57", country: "CO", name: "Colombia", flag: "🇨🇴"},
  {code: "+506", country: "CR", name: "Costa Rica", flag: "🇨🇷"},
  {code: "+1809", country: "DO", name: "Dominican Rep.", flag: "🇩🇴"},
  {code: "+593", country: "EC", name: "Ecuador", flag: "🇪🇨"},
  {code: "+52", country: "MX", name: "Mexico", flag: "🇲🇽"},
  {code: "+507", country: "PA", name: "Panama", flag: "🇵🇦"},
  {code: "+595", country: "PY", name: "Paraguay", flag: "🇵🇾"},
  {code: "+51", country: "PE", name: "Peru", flag: "🇵🇪"},
  {code: "+1787", country: "PR", name: "Puerto Rico", flag: "🇵🇷"},
  {code: "+598", country: "UY", name: "Uruguay", flag: "🇺🇾"},
  {code: "+58", country: "VE", name: "Venezuela", flag: "🇻🇪"},
  {code: "+93", country: "AF", name: "Afghanistan", flag: "🇦🇫"},
  {code: "+973", country: "BH", name: "Bahrain", flag: "🇧🇭"},
  {code: "+880", country: "BD", name: "Bangladesh", flag: "🇧🇩"},
  {code: "+975", country: "BT", name: "Bhutan", flag: "🇧🇹"},
  {code: "+673", country: "BN", name: "Brunei", flag: "🇧🇳"},
  {code: "+855", country: "KH", name: "Cambodia", flag: "🇰🇭"},
  {code: "+86", country: "CN", name: "China", flag: "🇨🇳"},
  {code: "+852", country: "HK", name: "Hong Kong", flag: "🇭🇰"},
  {code: "+62", country: "ID", name: "Indonesia", flag: "🇮🇩"},
  {code: "+98", country: "IR", name: "Iran", flag: "🇮🇷"},
  {code: "+964", country: "IQ", name: "Iraq", flag: "🇮🇶"},
  {code: "+972", country: "IL", name: "Israel", flag: "🇮🇱"},
  {code: "+81", country: "JP", name: "Japan", flag: "🇯🇵"},
  {code: "+962", country: "JO", name: "Jordan", flag: "🇯🇴"},
  {code: "+7", country: "KZ", name: "Kazakhstan", flag: "🇰🇿"},
  {code: "+965", country: "KW", name: "Kuwait", flag: "🇰🇼"},
  {code: "+996", country: "KG", name: "Kyrgyzstan", flag: "🇰🇬"},
  {code: "+856", country: "LA", name: "Laos", flag: "🇱🇦"},
  {code: "+961", country: "LB", name: "Lebanon", flag: "🇱🇧"},
  {code: "+853", country: "MO", name: "Macao", flag: "🇲🇴"},
  {code: "+960", country: "MV", name: "Maldives", flag: "🇲🇻"},
  {code: "+976", country: "MN", name: "Mongolia", flag: "🇲🇳"},
  {code: "+95", country: "MM", name: "Myanmar", flag: "🇲🇲"},
  {code: "+977", country: "NP", name: "Nepal", flag: "🇳🇵"},
  {code: "+968", country: "OM", name: "Oman", flag: "🇴🇲"},
  {code: "+92", country: "PK", name: "Pakistan", flag: "🇵🇰"},
  {code: "+63", country: "PH", name: "Philippines", flag: "🇵🇭"},
  {code: "+974", country: "QA", name: "Qatar", flag: "🇶🇦"},
  {code: "+966", country: "SA", name: "Saudi Arabia", flag: "🇸🇦"},
  {code: "+82", country: "KR", name: "South Korea", flag: "🇰🇷"},
  {code: "+94", country: "LK", name: "Sri Lanka", flag: "🇱🇰"},
  {code: "+963", country: "SY", name: "Syria", flag: "🇸🇾"},
  {code: "+886", country: "TW", name: "Taiwan", flag: "🇹🇼"},
  {code: "+992", country: "TJ", name: "Tajikistan", flag: "🇹🇯"},
  {code: "+66", country: "TH", name: "Thailand", flag: "🇹🇭"},
  {code: "+90", country: "TR", name: "Turkey", flag: "🇹🇷"},
  {code: "+993", country: "TM", name: "Turkmenistan", flag: "🇹🇲"},
  {code: "+998", country: "UZ", name: "Uzbekistan", flag: "🇺🇿"},
  {code: "+84", country: "VN", name: "Vietnam", flag: "🇻🇳"},
  {code: "+967", country: "YE", name: "Yemen", flag: "🇾🇪"},
  {code: "+355", country: "AL", name: "Albania", flag: "🇦🇱"},
  {code: "+43", country: "AT", name: "Austria", flag: "🇦🇹"},
  {code: "+375", country: "BY", name: "Belarus", flag: "🇧🇾"},
  {code: "+32", country: "BE", name: "Belgium", flag: "🇧🇪"},
  {code: "+387", country: "BA", name: "Bosnia & Herz.", flag: "🇧🇦"},
  {code: "+359", country: "BG", name: "Bulgaria", flag: "🇧🇬"},
  {code: "+385", country: "HR", name: "Croatia", flag: "🇭🇷"},
  {code: "+357", country: "CY", name: "Cyprus", flag: "🇨🇾"},
  {code: "+420", country: "CZ", name: "Czech Republic", flag: "🇨🇿"},
  {code: "+45", country: "DK", name: "Denmark", flag: "🇩🇰"},
  {code: "+372", country: "EE", name: "Estonia", flag: "🇪🇪"},
  {code: "+358", country: "FI", name: "Finland", flag: "🇫🇮"},
  {code: "+33", country: "FR", name: "France", flag: "🇫🇷"},
  {code: "+49", country: "DE", name: "Germany", flag: "🇩🇪"},
  {code: "+30", country: "GR", name: "Greece", flag: "🇬🇷"},
  {code: "+36", country: "HU", name: "Hungary", flag: "🇭🇺"},
  {code: "+354", country: "IS", name: "Iceland", flag: "🇮🇸"},
  {code: "+353", country: "IE", name: "Ireland", flag: "🇮🇪"},
  {code: "+39", country: "IT", name: "Italy", flag: "🇮🇹"},
  {code: "+371", country: "LV", name: "Latvia", flag: "🇱🇻"},
  {code: "+370", country: "LT", name: "Lithuania", flag: "🇱🇹"},
  {code: "+352", country: "LU", name: "Luxembourg", flag: "🇱🇺"},
  {code: "+356", country: "MT", name: "Malta", flag: "🇲🇹"},
  {code: "+373", country: "MD", name: "Moldova", flag: "🇲🇩"},
  {code: "+31", country: "NL", name: "Netherlands", flag: "🇳🇱"},
  {code: "+47", country: "NO", name: "Norway", flag: "🇳🇴"},
  {code: "+48", country: "PL", name: "Poland", flag: "🇵🇱"},
  {code: "+351", country: "PT", name: "Portugal", flag: "🇵🇹"},
  {code: "+40", country: "RO", name: "Romania", flag: "🇷🇴"},
  {code: "+7", country: "RU", name: "Russia", flag: "🇷🇺"},
  {code: "+381", country: "RS", name: "Serbia", flag: "🇷🇸"},
  {code: "+421", country: "SK", name: "Slovakia", flag: "🇸🇰"},
  {code: "+386", country: "SI", name: "Slovenia", flag: "🇸🇮"},
  {code: "+34", country: "ES", name: "Spain", flag: "🇪🇸"},
  {code: "+46", country: "SE", name: "Sweden", flag: "🇸🇪"},
  {code: "+41", country: "CH", name: "Switzerland", flag: "🇨🇭"},
  {code: "+380", country: "UA", name: "Ukraine", flag: "🇺🇦"},
  {code: "+213", country: "DZ", name: "Algeria", flag: "🇩🇿"},
  {code: "+244", country: "AO", name: "Angola", flag: "🇦🇴"},
  {code: "+237", country: "CM", name: "Cameroon", flag: "🇨🇲"},
  {code: "+20", country: "EG", name: "Egypt", flag: "🇪🇬"},
  {code: "+251", country: "ET", name: "Ethiopia", flag: "🇪🇹"},
  {code: "+233", country: "GH", name: "Ghana", flag: "🇬🇭"},
  {code: "+254", country: "KE", name: "Kenya", flag: "🇰🇪"},
  {code: "+218", country: "LY", name: "Libya", flag: "🇱🇾"},
  {code: "+212", country: "MA", name: "Morocco", flag: "🇲🇦"},
  {code: "+234", country: "NG", name: "Nigeria", flag: "🇳🇬"},
  {code: "+27", country: "ZA", name: "South Africa", flag: "🇿🇦"},
  {code: "+249", country: "SD", name: "Sudan", flag: "🇸🇩"},
  {code: "+255", country: "TZ", name: "Tanzania", flag: "🇹🇿"},
  {code: "+216", country: "TN", name: "Tunisia", flag: "🇹🇳"},
  {code: "+256", country: "UG", name: "Uganda", flag: "🇺🇬"},
  {code: "+64", country: "NZ", name: "New Zealand", flag: "🇳🇿"},
  {code: "+679", country: "FJ", name: "Fiji", flag: "🇫🇯"},
  {code: "+685", country: "WS", name: "Samoa", flag: "🇼🇸"},
  {code: "+675", country: "PG", name: "Papua New Guinea", flag: "🇵🇬"}
];

// ENHANCED: Tracking data extraction function
const getTrackingData = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const locale = navigator.language || 'en-US';

  let detectedCountry = 'Unknown';
  if (timezone && timezone.includes('/')) {
    const cityTimezone = timezone.split('/')[1];
    if (cityTimezone) {
      const timezoneCountryMap = {
        'Kolkata': 'India',
        'Mumbai': 'India',
        'Delhi': 'India',
        'Chennai': 'India',
        'Bangalore': 'India',
        'New_York': 'USA',
        'Los_Angeles': 'USA',
        'Chicago': 'USA',
        'London': 'UK',
        'Paris': 'France',
        'Berlin': 'Germany',
        'Tokyo': 'Japan',
        'Singapore': 'Singapore',
        'Dubai': 'UAE'
      };
      detectedCountry = timezoneCountryMap[cityTimezone] || 'Unknown';
    }
  }

  return {
    campaign_id: urlParams.get('campaign_id') || '',
    adset_id: urlParams.get('adset_id') || '',
    ad_id: urlParams.get('ad_id') || '',
    fbclid: urlParams.get('fbclid') || '',
    gclid: urlParams.get('gclid') || '',
    campaign_name: urlParams.get('campaign_name') || 'AI Workshop Campaign',
    adset_name: urlParams.get('adset_name') || 'Workshop Ad Set',
    ad_name: urlParams.get('ad_name') || 'Workshop Ad',
    utm_source: urlParams.get('utm_source') || '',
    utm_medium: urlParams.get('utm_medium') || '',
    utm_campaign: urlParams.get('utm_campaign') || '',
    utm_term: urlParams.get('utm_term') || '',
    utm_content: urlParams.get('utm_content') || '',
    source: urlParams.get('source') || 'organic',
    medium: urlParams.get('medium') || '',
    country: detectedCountry,
    timezone: timezone,
    locale: locale,
    referrer: document.referrer || '',
    page_url: window.location.href,
    page_title: document.title,
    user_agent: navigator.userAgent,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    timestamp: new Date().toISOString(),
    session_id: sessionStorage.getItem('session_id') || Date.now().toString()
  };
};

// Generate workshop batch ID
const generateWorkshopBatchId = (workshopDate) => {
  if (!workshopDate) return 'BATCH_UNKNOWN';
  const date = new Date(workshopDate);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `BATCH_${year}${month}${day}`;
};

// Get batch name
const getBatchName = (workshopDate) => {
  if (!workshopDate) return 'Sunday Unknown';
  const date = new Date(workshopDate);
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayName = dayNames[date.getDay()];
  const day = date.getDate();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthName = monthNames[date.getMonth()];
  return `${dayName} ${day}${getDaySuffix(day)} ${monthName}`;
};

function getDaySuffix(day) {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

// Searchable Country Code Component
const CountryOption = React.memo(({country, onSelect}) => (
  <li
    className="country-option"
    onClick={() => onSelect(country.code)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') onSelect(country.code);
    }}
    role="option"
    tabIndex={0}
  >
    <span className="country-flag">{country.flag}</span>
    <span className="country-name">{country.name}</span>
    <span className="country-code">{country.code}</span>
  </li>
));

function SearchableCountryCodeSelect({value, onChange, options}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedCountry = useMemo(
    () => options.find(c => c.code === value) || options[0],
    [value, options]
  );

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const lowerSearch = searchTerm.toLowerCase();
    return options.filter(
      country =>
        country.name.toLowerCase().includes(lowerSearch) ||
        country.code.includes(lowerSearch) ||
        country.country.toLowerCase().includes(lowerSearch)
    );
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (code) => {
    onChange(code);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="searchable-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className="country-select-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="country-flag">{selectedCountry.flag}</span>
        <span>{selectedCountry.code}</span>
        <FiChevronDown className={`chevron-icon ${isOpen ? 'expanded' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="dropdown-menu"
            initial={{opacity: 0, y: -10}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: -10}}
            transition={{duration: 0.2}}
          >
            <div className="search-input-wrapper">
              <FiSearch className="search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search country..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <ul className="dropdown-list" role="listbox">
              {filteredOptions.length > 0 ? (
                filteredOptions.map(country => (
                  <CountryOption
                    key={country.country}
                    country={country}
                    onSelect={handleSelect}
                  />
                ))
              ) : (
                <li className="country-option-empty">No results found</li>
              )}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function App() {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+91',
    phone: '',
    profession: '',
    consent: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [faqSearch, setFaqSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('all');
  const [expandedFeedback, setExpandedFeedback] = useState({});
  const [hasAutoPopupShown, setHasAutoPopupShown] = useState(false);

  const modalRef = useRef(null);
  const firstInputRef = useRef(null);
  const autoPopupTimeoutRef = useRef(null);

  const {workshopDetails} = useWorkshopSchedule();

  useEffect(() => {
    if (!sessionStorage.getItem('session_id')) {
      sessionStorage.setItem('session_id', Date.now().toString());
    }
  }, []);

  useEffect(() => {
    if (!hasAutoPopupShown && !isFormModalOpen) {
      autoPopupTimeoutRef.current = setTimeout(() => {
        setIsFormModalOpen(true);
        setHasAutoPopupShown(true);
        if (typeof fbq !== 'undefined') {
          fbq('track', 'ViewContent', {
            content_name: 'Sales Automation Workshop - Auto Popup',
            content_category: 'Workshop',
            content_type: 'auto_popup',
            value: 97,
            currency: 'INR'
          });
        }
      }, 10000);
    }

    return () => {
      if (autoPopupTimeoutRef.current) {
        clearTimeout(autoPopupTimeoutRef.current);
        autoPopupTimeoutRef.current = null;
      }
    };
  }, [hasAutoPopupShown, isFormModalOpen]);

  useEffect(() => {
    if (isFormModalOpen && firstInputRef.current) {
      firstInputRef.current.focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsFormModalOpen(false);
        setSelectedVideo(null);
      }
    };

    if (isFormModalOpen || selectedVideo) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isFormModalOpen, selectedVideo]);

  const handleFaqSearch = useCallback((value) => {
    setFaqSearch(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    const newTimeout = setTimeout(() => {}, 300);
    setSearchTimeout(newTimeout);
  }, [searchTimeout]);

  const filteredFaqs = useMemo(() => {
    if (!faqSearch.trim()) return faqData;
    return faqData.filter(
      faq =>
        faq.question.toLowerCase().includes(faqSearch.toLowerCase()) ||
        faq.answer.toLowerCase().includes(faqSearch.toLowerCase())
    );
  }, [faqSearch]);

  const filteredFeedbacks = useMemo(() => {
    if (selectedRoleFilter === 'all') return clientFeedbacks;
    return clientFeedbacks.filter(feedback =>
      feedback.role.toLowerCase().includes(selectedRoleFilter.toLowerCase())
    );
  }, [selectedRoleFilter]);

  const uniqueRoles = useMemo(
    () => ['all', ...new Set(clientFeedbacks.map(f => f.role))],
    []
  );

  const toggleFaq = (faqId) => setExpandedFaq(expandedFaq === faqId ? null : faqId);

  const toggleFeedbackExpanded = (feedbackId) =>
    setExpandedFeedback(prev => ({...prev, [feedbackId]: !prev[feedbackId]}));

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      errors.email = 'Please enter a valid email';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    else if (!/^\d{7,15}$/.test(formData.phone.replace(/\s/g, '')))
      errors.phone = 'Please enter a valid phone number';
    if (!formData.profession.trim())
      errors.profession = 'Profession/Role is required';
    if (!formData.consent) errors.consent = 'Please agree to the terms';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  //===UPDATED: Complete payment URL builder with ALL parameters including ISO date===
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const phoneWithCountryCode = `${formData.countryCode}${formData.phone}`;
      const trackingData = getTrackingData();
      const workshopBatchId = generateWorkshopBatchId(workshopDetails.date);
      const workshopBatchName = getBatchName(workshopDetails.date);
      const selectedCountry = countryCodes.find(
        c => c.code === formData.countryCode
      ) || {country: 'Unknown', name: 'Unknown'};

      // Prepare webhook payload
      const webhookPayload = {
        name: formData.name,
        email: formData.email,
        phone: phoneWithCountryCode,
        profession: formData.profession,
        campaign_id: trackingData.campaign_id,
        campaign_name: trackingData.campaign_name,
        adset_id: trackingData.adset_id,
        adset_name: trackingData.adset_name,
        ad_id: trackingData.ad_id,
        ad_name: trackingData.ad_name,
        country: trackingData.country,
        timezone: trackingData.timezone,
        locale: trackingData.locale,
        phone_country_code: selectedCountry.country,
        phone_country_name: selectedCountry.name,
        phone_dial_code: formData.countryCode,
        batch_id: workshopBatchId,
        batch_name: workshopBatchName,
        batch_date: workshopDetails.formattedDate,
        batch_date_iso: workshopDetails.isoDate, // NEW: ISO 8601 format
        batch_time: workshopDetails.timeString,
        batch_day: workshopDetails.dayOfWeek || 'Sunday',
        batch_duration: workshopDetails.duration || '3 hours',
        batch_timezone: workshopDetails.timezone || 'IST',
        batch_status: workshopDetails.status || 'upcoming',
        utm_source: trackingData.utm_source,
        utm_medium: trackingData.utm_medium,
        utm_campaign: trackingData.utm_campaign,
        utm_term: trackingData.utm_term,
        utm_content: trackingData.utm_content,
        fbclid: trackingData.fbclid,
        gclid: trackingData.gclid,
        source: trackingData.source,
        medium: trackingData.medium,
        referrer: trackingData.referrer,
        page_url: trackingData.page_url,
        page_title: trackingData.page_title,
        user_agent: trackingData.user_agent,
        screen_width: trackingData.screen_width,
        screen_height: trackingData.screen_height,
        session_id: trackingData.session_id,
        registration_timestamp: trackingData.timestamp,
        registration_source: 'website_form',
        conversion_type: 'paid_registration',
        product_name: '10x AI Agent Workshop',
        product_price: 97,
        currency: 'INR'
      };

      // Send to webhook
      try {
        const webhookResponse = await fetch(WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(webhookPayload)
        });

        if (!webhookResponse.ok) {
          console.warn('Webhook response not OK:', webhookResponse.status);
        }

        console.log('Enhanced webhook data sent successfully:', webhookPayload);
      } catch (webhookError) {
        console.warn('Webhook submission failed:', webhookError);
      }

      // Track Facebook Pixel
      if (typeof fbq !== 'undefined') {
        fbq('track', 'CompleteRegistration', {
          content_name: 'Sales Automation Workshop Registration',
          content_category: 'Workshop',
          value: 97,
          currency: 'INR',
          ...(trackingData.campaign_id && {campaign_id: trackingData.campaign_id}),
          ...(trackingData.adset_id && {adset_id: trackingData.adset_id}),
          ...(trackingData.ad_id && {ad_id: trackingData.ad_id}),
          custom_batch_id: workshopBatchId,
          custom_batch_name: workshopBatchName
        });
      }

      setShowSuccess(true);
      setIsFormModalOpen(false);
      setFormData({
        name: '',
        email: '',
        countryCode: '+91',
        phone: '',
        profession: '',
        consent: false
      });

      //===UPDATED: Build payment URL with ALL parameters properly===
      const paymentParams = new URLSearchParams();

      // Required base parameters
      paymentParams.append('staffId', '65130');
      paymentParams.append('ac', '1');

      // User information (Multiple redundant fields for compatibility)
      const nameFields = ['first_name', 'name', 'full_name', 'user_name', 'contact_name', 'billing_name'];
      nameFields.forEach(field => paymentParams.append(field, formData.name));

      const emailFields = ['email', 'email_address', 'user_email', 'contact_email', 'billing_email'];
      emailFields.forEach(field => paymentParams.append(field, formData.email));

      const phoneFields = ['phone_no', 'phone', 'phone_number', 'mobile', 'mobile_number', 'contact_phone', 'billing_phone'];
      phoneFields.forEach(field => paymentParams.append(field, phoneWithCountryCode));

      const professionFields = ['customer_profession', 'profession'];
      professionFields.forEach(field => paymentParams.append(field, formData.profession));

      // NEW: Country code and name in UTM link
      paymentParams.append('country', selectedCountry.name);
      paymentParams.append('country_code', selectedCountry.country);
      paymentParams.append('phone_country_code', selectedCountry.country);
      paymentParams.append('phone_country_name', selectedCountry.name);
      paymentParams.append('phone_dial_code', formData.countryCode);

      // Workshop details (Redundant fields)
      const batchIdFields = ['batch_id', 'workshop_batch', 'event_batch', 'course_batch', 'batch'];
      batchIdFields.forEach(field => paymentParams.append(field, workshopBatchId));

      const dateFields = ['batch_date', 'workshop_date', 'event_date', 'webinar_date'];
      dateFields.forEach(field => paymentParams.append(field, workshopDetails.formattedDate));

      // NEW: Webinar Presentation Date in ISO 8601 format
      const isoDateFields = ['webinar_presentation_date', 'batch_date_iso', 'workshop_date_iso'];
      isoDateFields.forEach(field => paymentParams.append(field, workshopDetails.isoDate));

      const timeFields = ['batch_time', 'workshop_time', 'event_time'];
      timeFields.forEach(field => paymentParams.append(field, workshopDetails.timeString));

      paymentParams.append('batch_day', workshopDetails.dayOfWeek || 'Sunday');
      paymentParams.append('batch_duration', workshopDetails.duration || '3 hours');
      paymentParams.append('batch_timezone', workshopDetails.timezone || 'IST');
      paymentParams.append('batch_status', 'Workshop in 3 days');

      // Payment details
      paymentParams.append('amount', '97');
      paymentParams.append('currency', 'INR');
      paymentParams.append('product_name', '10x AI Agent Workshop');

      // Meta Ads tracking
      if (trackingData.campaign_id) paymentParams.append('campaign_id', trackingData.campaign_id);
      if (trackingData.adset_id) paymentParams.append('adset_id', trackingData.adset_id);
      if (trackingData.ad_id) paymentParams.append('ad_id', trackingData.ad_id);
      if (trackingData.campaign_name) paymentParams.append('campaign_name', trackingData.campaign_name);
      if (trackingData.adset_name) paymentParams.append('adset_name', trackingData.adset_name);
      if (trackingData.ad_name) paymentParams.append('ad_name', trackingData.ad_name);

      // Click IDs
      if (trackingData.fbclid) paymentParams.append('fbclid', trackingData.fbclid);
      if (trackingData.gclid) paymentParams.append('gclid', trackingData.gclid);

      // UTM parameters
      if (trackingData.utm_source) paymentParams.append('utm_source', trackingData.utm_source);
      if (trackingData.utm_medium) paymentParams.append('utm_medium', trackingData.utm_medium);
      if (trackingData.utm_campaign) paymentParams.append('utm_campaign', trackingData.utm_campaign);
      if (trackingData.utm_term) paymentParams.append('utm_term', trackingData.utm_term);
      if (trackingData.utm_content) paymentParams.append('utm_content', trackingData.utm_content);

      // Additional tracking
      paymentParams.append('source', trackingData.source);
      if (trackingData.medium) paymentParams.append('medium', trackingData.medium);
      if (trackingData.referrer) paymentParams.append('referrer', trackingData.referrer);

      // Session tracking
      paymentParams.append('session_id', trackingData.session_id);
      paymentParams.append('registration_timestamp', trackingData.timestamp);
      paymentParams.append('page_url', trackingData.page_url);
      paymentParams.append('page_title', trackingData.page_title);
      paymentParams.append('user_agent', trackingData.user_agent);

      // Build final URL
      const finalPaymentUrl = `${PAYMENT_PAGE_BASE_URL}?${paymentParams.toString()}`;

      console.log('Complete Payment URL:', finalPaymentUrl);
      console.log('Payment Parameters:', Object.fromEntries(paymentParams));

      // Redirect to payment page
      setTimeout(() => {
        window.location.href = finalPaymentUrl;
      }, 1500);

      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({...prev, [field]: value}));
    if (formErrors[field]) setFormErrors(prev => ({...prev, [field]: ''}));
  };

  const getInitials = (name) =>
    name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const handleManualModalOpen = () => {
    if (autoPopupTimeoutRef.current) {
      clearTimeout(autoPopupTimeoutRef.current);
      autoPopupTimeoutRef.current = null;
    }
    setHasAutoPopupShown(true);
    setIsFormModalOpen(true);
  };

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap');
          .app, .modal-content {
            font-family: 'Poppins', sans-serif;
          }
          .section-content h2, .mentor-info h2, .final-cta h2 {
            font-weight: 700;
          }

          .sticky-footer {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px 20px;
            background: linear-gradient(90deg, #0b1324, #101c33);
            color: white;
            z-index: 40;
            box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
          }

          .footer-info-box {
            background: rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            padding: 10px 16px;
            font-size: 0.9rem;
            font-weight: 600;
            text-align: left;
            line-height: 1.4;
          }

          .footer-info-box .date {
            color: #f3f4f6;
          }

          .footer-info-box .time {
            color: #fb923c;
          }

          .footer-cta-button {
            background: #fb923c;
            color: #1f2937;
            font-weight: 700;
            padding: 12px 20px;
            border-radius: 12px;
            border: none;
            cursor: pointer;
            font-size: 1rem;
            box-shadow: 0 4px 10px rgba(251, 146, 60, 0.3);
            flex-shrink: 0;
          }

          .app {
            padding-bottom: 90px;
          }

          @media (max-width: 640px) {
            .sticky-footer {
              display: none;
            }
            .app {
              padding-bottom: 0;
            }
            .footer-info-box {
              padding: 6px 10px;
              font-size: 0.75rem;
              line-height: 1.3;
            }
            .footer-info-box .time {
              font-size: 0.8rem;
            }
            .footer-cta-button {
              padding: 10px 12px;
              font-size: 0.9rem;
            }
          }

          .searchable-dropdown {
            position: relative;
            width: 120px;
            flex-shrink: 0;
          }

          .country-select-button {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            padding: 0 10px;
            height: 48px;
            background: #2c3e50;
            color: #ecf0f1;
            border: 1px solid #34495e;
            border-radius: 8px 0 0 8px;
            cursor: pointer;
            font-size: 0.9rem;
          }

          .country-select-button:hover {
            background: #34495e;
          }

          .country-select-button .country-flag {
            font-size: 1.2rem;
            margin-right: 6px;
          }

          .country-select-button .chevron-icon {
            transition: transform 0.2s ease;
          }

          .country-select-button .chevron-icon.expanded {
            transform: rotate(180deg);
          }

          .dropdown-menu {
            position: absolute;
            bottom: 105%;
            left: 0;
            width: 300px;
            max-height: 250px;
            background: #2c3e50;
            border: 1px solid #34495e;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
            z-index: 1001;
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }

          @media (max-height: 500px) {
            .dropdown-menu {
              bottom: auto;
              top: 105%;
            }
          }

          .search-input-wrapper {
            position: relative;
            padding: 8px;
            border-bottom: 1px solid #34495e;
          }

          .search-input-wrapper .search-icon {
            position: absolute;
            left: 16px;
            top: 50%;
            transform: translateY(-50%);
            color: #7f8c8d;
          }

          .search-input-wrapper .search-input {
            width: 100%;
            padding: 8px 12px 8px 34px;
            background: #1f2937;
            border: 1px solid #34495e;
            border-radius: 6px;
            color: #ecf0f1;
          }

          .search-input-wrapper .search-input::placeholder {
            color: #7f8c8d;
          }

          .dropdown-list {
            list-style: none;
            margin: 0;
            padding: 0;
            overflow-y: auto;
            flex-grow: 1;
          }

          .country-option {
            display: flex;
            align-items: center;
            padding: 10px 12px;
            cursor: pointer;
            transition: background-color 0.2s ease;
          }

          .country-option:hover, .country-option:focus {
            background-color: #34495e;
          }

          .country-option .country-flag {
            font-size: 1.2rem;
            margin-right: 10px;
          }

          .country-option .country-name {
            flex-grow: 1;
            font-size: 0.9rem;
            color: #ecf0f1;
          }

          .country-option .country-code {
            font-size: 0.9rem;
            color: #7f8c8d;
          }

          .country-option-empty {
            padding: 10px 12px;
            color: #7f8c8d;
            text-align: center;
          }

          .phone-input-group {
            display: flex;
          }

          .phone-input-group input[type="tel"] {
            border-radius: 0 8px 8px 0;
            flex-grow: 1;
          }
        `}
      </style>

      <div className="app">
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              className="success-toast"
              initial={{opacity: 0, y: -50}}
              animate={{opacity: 1, y: 0}}
              exit={{opacity: 0, y: -50}}
              transition={{type: "spring", stiffness: 300, damping: 20}}
            >
              <FiCheck className="success-icon" />
              Registration successful! Redirecting to payment...
            </motion.div>
          )}
        </AnimatePresence>

        <RecentRegistrations />

        <HeroSection openRegistration={handleManualModalOpen} />

        {/* Problem Section */}
        <motion.section
          className="problem-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}>
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                😰 இந்த பிரச்சினைகள் உங்களுக்கு இருக்கிறதா?
              </motion.h2>

              <motion.div
                className="problem-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{once: true, amount: 0.2}}>
                {[
                  {
                    emoji: "💸",
                    title: "Revenue குறைவாக இருக்கிறது",
                    desc: "மாதம் மாதம் same revenue-தான். Growth இல்லை, stress மட்டும் தான் அதிகம்."
                  },
                  {
                    emoji: "⏰",
                    title: "Time இல்லை",
                    desc: "24/7 வேலை செய்தாலும் வளர்ச்சி இல்லை. Personal life-ம் sacrifice ஆகிறது."
                  },
                  {
                    emoji: "🤖",
                    title: "AI பற்றிய குழப்பம்",
                    desc: "AI tools எல்லாம் English-ல் இருக்கு. எங்க business-க்கு அதை எப்படி use பண்றதுன்னே தெரியல."
                  },
                  {
                    emoji: "💰",
                    title: "Marketing budget waste",
                    desc: "Facebook ads, Google ads-ல் பணம் போட்டும் சரியான ROI கிடைக்கல."
                  }
                ].map((problem) => (
                  <motion.div
                    key={problem.title}
                    className="problem-card"
                    variants={itemVariants}
                    whileHover={{y: -8, transition: {duration: 0.2}}}>
                    <h3>{problem.emoji} {problem.title}</h3>
                    <p>{problem.desc}</p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Solution Section */}
        <motion.section
          className="solution-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}>
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                🎯 Old Way vs 10xAI Method
              </motion.h2>

              <div className="comparison-table">
                <motion.div
                  className="old-way"
                  {...sectionFadeIn}
                  transition={{delay: 0.2, ...sectionFadeIn.transition}}>
                  <h3>❌ பழைய முறை</h3>
                  <ul>
                    <li>Manual-ஆக எல்லாவற்றையும் செய்வது</li>
                    <li>Guesswork-ல் marketing செய்வது</li>
                    <li>அதிக நேர விரயம் (Time waste)</li>
                    <li>Inconsistent results</li>
                    <li>High operational cost</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="new-way"
                  {...sectionFadeIn}
                  transition={{delay: 0.4, ...sectionFadeIn.transition}}>
                  <h3>✅ 10xAI Method</h3>
                  <ul>
                    <li>அனைத்து Process-களிலும் AI Automation</li>
                    <li>Data-driven marketing decisions</li>
                    <li>80% வரை நேர சேமிப்பு (time saving)</li>
                    <li>Predictable growth</li>
                    <li>புதிய ஆட்கள் இல்லாமல் business-ஐ scale செய்வது</li>
                  </ul>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Mentor Section */}
        <section className="mentor-section py-16 bg-[#0b1324] text-gray-200">
          <div className="container mx-auto px-4">
            <motion.div
              className="mentor-content grid items-center gap-10 lg:grid-cols-2"
              initial="initial"
              whileInView="whileInView"
              transition={sectionFadeIn.transition}
              viewport={sectionFadeIn.viewport}>
              <div className="flex justify-center lg:justify-end">
                <motion.div
                  className="rounded-3xl border-2 border-orange-400 p-3 shadow-lg shadow-orange-500/20 bg-[#101c33]"
                  whileHover={{scale: 1.02}}
                  transition={{duration: 0.3}}>
                  <img
                    src="https://storage.files-vault.com/uploads/1761032692-VSdwsp8AG0.png"
                    alt="Abdul Hakkim - AI Business Mentor"
                    loading="lazy"
                    className="rounded-2xl w-full max-w-[360px] md:max-w-[400px] object-cover"
                  />
                </motion.div>
              </div>

              <div className="mentor-info max-w-xl mx-auto lg:mx-0">
                <motion.h2
                  className="text-3xl md:text-4xl font-bold text-orange-400 mb-6 flex items-center gap-2"
                  {...sectionFadeIn}>
                  🎯 <span>Meet Your Mentor</span>
                </motion.h2>

                <motion.p
                  className="text-base md:text-lg leading-relaxed mb-6 text-gray-300"
                  {...sectionFadeIn}
                  transition={{delay: 0.1, ...sectionFadeIn.transition}}>
                  நான் Abdul Hakkim. Manual work-ல time waste பண்ணி, leads & sales இல்லாம ரொம்ப கஷ்டப்பட்டேன். Real problem என்னனு அப்போ தான் realise பண்ணேன்...
                </motion.p>

                <motion.ul
                  className="space-y-4 text-gray-300 text-base md:text-lg"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{once: true, amount: 0.2}}>
                  {[
                    {
                      emoji: "👉",
                      color: "text-yellow-400",
                      title: "The Real Reason:",
                      desc: "Proper tools-உம், ஒரு நல்ல automation system-உம் இல்லாதது தான்."
                    },
                    {
                      emoji: "✅",
                      color: "text-green-400",
                      title: "My Solution:",
                      desc: "நான் create பண்ண AI System உங்களுக்காக leads generate பண்ணும், auto follow-up பண்ணும் & உங்க நேரத்த save பண்ணும்."
                    },
                    {
                      emoji: "📈",
                      color: "text-blue-400",
                      title: "Proven Results:",
                      desc: "500+ businesses, 10x leads growth, 3x sales increase."
                    },
                    {
                      emoji: "🗣️",
                      color: "text-purple-400",
                      title: "Easy Training:",
                      desc: "Tamil-ல step-by-step training. Zero coding, 100% result நிச்சயம்."
                    }
                  ].map((item) => (
                    <motion.li
                      key={item.title}
                      className="flex items-start gap-3"
                      variants={itemVariants}>
                      <span className={`${item.color} text-xl mt-1`}>{item.emoji}</span>
                      <div className="flex-1">
                        <span className="font-medium">{item.title}</span>
                        <div className="text-gray-300 mt-1">{item.desc}</div>
                      </div>
                    </motion.li>
                  ))}
                </motion.ul>

                <motion.p
                  className="text-base md:text-lg leading-relaxed mt-6 text-gray-300"
                  {...sectionFadeIn}
                  transition={{delay: 0.2, ...sectionFadeIn.transition}}>
                  இன்னும் "hope" stage-ல இருக்கீங்களா? இது தான் உங்க turning point — AI use பண்ணி business-அ next level-க்கு கொண்டு போங்க! 💪
                </motion.p>

                <motion.div
                  className="mt-6 flex flex-wrap gap-4"
                  {...sectionFadeIn}
                  transition={{delay: 0.3, ...sectionFadeIn.transition}}>
                  <div className="flex items-center gap-2 text-sm bg-gray-700 px-3 py-1 rounded-full">
                    <FiStar className="text-yellow-400" />
                    <span>500+ Successful AI Implementations</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm bg-gray-700 px-3 py-1 rounded-full">
                    <FiStar className="text-yellow-400" />
                    <span>₹10+ Crore Revenue Generated</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm bg-gray-700 px-3 py-1 rounded-full">
                    <FiStar className="text-yellow-400" />
                    <span>Tamil + English AI Training Expert</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Testimonials Section */}
        <motion.section
          className="testimonials-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}>
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                🎬 Success Stories - Video Testimonials
              </motion.h2>

              <motion.p
                className="testimonials-subtitle"
                {...sectionFadeIn}
                transition={{delay: 0.1, ...sectionFadeIn.transition}}>
                Real results from real Tamil entrepreneurs who transformed their businesses with AI
              </motion.p>

              <motion.div
                className="video-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{once: true, amount: 0.2}}>
                {testimonialVideos.map((video) => (
                  <motion.div
                    key={video.id}
                    className="video-card"
                    onClick={() => {
                      setSelectedVideo(video);
                      if (typeof fbq !== 'undefined') {
                        fbq('track', 'ViewContent', {
                          content_name: video.title,
                          content_type: 'video'
                        });
                      }
                    }}
                    variants={itemVariants}
                    whileHover={{scale: 1.02, y: -8}}
                    whileTap={{scale: 0.98}}
                    tabIndex={0}
                    role="button"
                    aria-label={`Play testimonial video from ${video.name}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedVideo(video);
                      }
                    }}
                  >
                    <div className="video-thumbnail">
                      <img
                        src={video.thumbnail}
                        alt={`${video.title} thumbnail`}
                        loading="lazy"
                        width="300"
                        height="200"
                      />
                      <div className="play-overlay">
                        <FiPlay className="play-icon" />
                      </div>
                    </div>
                    <div className="video-info">
                      <h4>{video.title}</h4>
                      <p>{video.name}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Client Feedback Section */}
        <motion.section
          className="client-feedback-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}>
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                What Our Clients Say
              </motion.h2>

              <motion.p
                className="feedback-subtitle"
                {...sectionFadeIn}
                transition={{delay: 0.1, ...sectionFadeIn.transition}}>
                Real success stories from Tamil entrepreneurs who transformed their businesses
              </motion.p>

              <motion.div
                className="feedback-filter"
                {...sectionFadeIn}
                transition={{delay: 0.2, ...sectionFadeIn.transition}}>
                <FiFilter className="filter-icon" />
                <select
                  value={selectedRoleFilter}
                  onChange={(e) => setSelectedRoleFilter(e.target.value)}
                  className="role-filter-select"
                  aria-label="Filter by role"
                >
                  {uniqueRoles.map(role => (
                    <option key={role} value={role}>
                      {role === 'all' ? 'All Roles' : role}
                    </option>
                  ))}
                </select>
              </motion.div>

              <motion.div
                className="feedback-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{once: true, amount: 0.2}}>
                {filteredFeedbacks.map((feedback) => (
                  <motion.div
                    key={feedback.id}
                    className="feedback-card"
                    variants={itemVariants}
                    whileHover={{scale: 1.02, y: -8}}
                    whileTap={{scale: 0.98}}
                    tabIndex={0}
                    role="article"
                    aria-labelledby={`feedback-name-${feedback.id}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleFeedbackExpanded(feedback.id);
                      }
                    }}
                  >
                    <div className="feedback-image-container">
                      {feedback.image ? (
                        <img
                          src={feedback.image}
                          alt={`${feedback.name} - ${feedback.role}`}
                          loading="lazy"
                          width="300"
                          height="200"
                          className="feedback-rect-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="avatar-fallback-rect">
                        {getInitials(feedback.name)}
                      </div>
                    </div>

                    <div className="feedback-info">
                      <h3 id={`feedback-name-${feedback.id}`}>{feedback.name}</h3>
                      <p className="feedback-role">{feedback.role}</p>
                    </div>

                    <div className="feedback-quote">
                      <p>
                        {expandedFeedback[feedback.id] || feedback.quote.length <= 140
                          ? feedback.quote
                          : `${feedback.quote.slice(0, 140)}...`}
                      </p>
                      {feedback.quote.length > 140 && (
                        <button
                          className="read-more-btn"
                          onClick={() => toggleFeedbackExpanded(feedback.id)}
                          aria-expanded={!!expandedFeedback[feedback.id]}
                          aria-controls={`feedback-quote-${feedback.id}`}
                        >
                          {expandedFeedback[feedback.id] ? 'Read less' : 'Read more'}
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Bonus Section */}
        <motion.section
          className="bonus-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}>
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                🎁 Exclusive Bonus Vault (Worth ₹25,000)
              </motion.h2>

              <motion.div
                className="bonus-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{once: true, amount: 0.2}}>
                {[
                  {
                    img: "https://storage.files-vault.com/uploads/1760966854-BXbDBFXvqB.png",
                    emoji: "🤖",
                    title: "AI Tools Starter Pack",
                    desc: "50+ verified AI tools list with Tamil tutorials (Worth ₹5,000)"
                  },
                  {
                    img: "https://storage.files-vault.com/uploads/1760966871-XKw3XTHpgg.png",
                    emoji: "📊",
                    title: "Business Automation Templates",
                    desc: "Ready-to-use automation workflows for Tamil businesses (Worth ₹8,000)"
                  },
                  {
                    img: "https://storage.files-vault.com/uploads/1760976790-fakmsXQpOV.png",
                    emoji: "💬",
                    title: "Private Tamil AI Community",
                    desc: "Lifetime access to exclusive Tamil entrepreneurs AI group (Worth ₹7,000)"
                  },
                  {
                    img: "https://storage.files-vault.com/uploads/1760977051-dmxHkhczed.png",
                    emoji: "📞",
                    title: "1-on-1 AI Strategy Call",
                    desc: "Personal 30-minute session to customize AI for your business (Worth ₹5,000)"
                  }
                ].map((bonus) => (
                  <motion.div
                    key={bonus.title}
                    className="bonus-item"
                    variants={itemVariants}
                    whileHover={{y: -8, scale: 1.02}}>
                    <img
                      src={bonus.img}
                      alt={bonus.title}
                      className="bonus-image"
                      width="120"
                      height="160"
                      loading="lazy"
                    />
                    <h3>{bonus.emoji} {bonus.title}</h3>
                    <p>{bonus.desc}</p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* FAQ Section */}
        <motion.section
          className="faq-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}>
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                Frequently Asked Questions
              </motion.h2>

              <motion.p
                className="faq-subtitle"
                {...sectionFadeIn}
                transition={{delay: 0.1, ...sectionFadeIn.transition}}>
                Got questions? We've got answers. Find everything you need to know about our program.
              </motion.p>

              <motion.div
                className="faq-search"
                {...sectionFadeIn}
                transition={{delay: 0.2, ...sectionFadeIn.transition}}>
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search questions..."
                  value={faqSearch}
                  onChange={(e) => handleFaqSearch(e.target.value)}
                  className="search-input"
                  aria-label="Search frequently asked questions"
                />
              </motion.div>

              <div className="faq-accordion">
                {filteredFaqs.map((faq) => (
                  <motion.div key={faq.id} className="faq-item" layout>
                    <button
                      className="faq-question"
                      onClick={() => toggleFaq(faq.id)}
                      aria-expanded={expandedFaq === faq.id}
                      aria-controls={`faq-answer-${faq.id}`}
                      id={`faq-question-${faq.id}`}
                    >
                      <span>{faq.question}</span>
                      <FiChevronDown
                        className={`chevron-icon ${
                          expandedFaq === faq.id ? 'expanded' : ''
                        }`}
                      />
                    </button>

                    <AnimatePresence>
                      {expandedFaq === faq.id && (
                        <motion.div
                          id={`faq-answer-${faq.id}`}
                          className="faq-answer"
                          role="region"
                          aria-labelledby={`faq-question-${faq.id}`}
                          initial={{height: 0, opacity: 0}}
                          animate={{height: 'auto', opacity: 1}}
                          exit={{height: 0, opacity: 0}}
                          transition={{duration: 0.3, ease: "easeInOut"}}
                        >
                          <div
                            className="faq-answer-content"
                            dangerouslySetInnerHTML={{__html: faq.answer}}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>

              {filteredFaqs.length === 0 && (
                <motion.div
                  className="faq-no-results"
                  initial={{opacity: 0}}
                  animate={{opacity: 1}}
                  transition={{duration: 0.3}}>
                  <p>No questions found matching your search. Try different keywords.</p>
                </motion.div>
              )}
            </motion.div>
          </div>
        </motion.section>

        {/* Final CTA Section */}
        <motion.section
          className="final-cta"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}>
          <div className="container">
            <motion.div className="cta-content">
              <motion.h2 {...sectionFadeIn}>
                🔥 Limited Time Offer - Only 50 Seats Available!
              </motion.h2>

              <motion.p
                className="cta-subtitle"
                {...sectionFadeIn}
                transition={{delay: 0.1, ...sectionFadeIn.transition}}>
                Workshop Value: ₹2,997 | Today Only: **Just ₹97!**
              </motion.p>

              <WorkshopTimer className="large" />

              <motion.button
                className="cta-primary large"
                onClick={() => {
                  handleManualModalOpen();
                  if (typeof fbq !== 'undefined') {
                    fbq('track', 'InitiateCheckout', {
                      content_name: 'Sales Automation Workshop - Final CTA',
                      content_category: 'Workshop',
                      value: 97,
                      currency: 'INR'
                    });
                  }
                }}
                {...sectionFadeIn}
                transition={{delay: 0.4, ...sectionFadeIn.transition}}
                whileHover={{scale: 1.05, y: -4}}
                whileTap={{scale: 0.95}}
              >
                Yes! Reserve My Seat for ₹97 🚀
              </motion.button>

              <motion.div
                className="guarantee-text"
                {...sectionFadeIn}
                transition={{delay: 0.5, ...sectionFadeIn.transition}}>
                <FiCheck className="check-icon" />
                <span>100% Value Guarantee - Worth ₹2,997</span>
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Registration Modal */}
        <AnimatePresence>
          {isFormModalOpen && (
            <motion.div
              className="modal-overlay"
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              exit={{opacity: 0}}
              onClick={(e) => {
                if (e.target === e.currentTarget) setIsFormModalOpen(false);
              }}
            >
              <motion.div
                className="modal-content"
                id="registration-modal"
                initial={{opacity: 0, scale: 0.8, y: 20}}
                animate={{opacity: 1, scale: 1, y: 0}}
                exit={{opacity: 0, scale: 0.8, y: 20}}
                transition={{type: "spring", stiffness: 300, damping: 25}}
                ref={modalRef}
              >
                <div className="modal-header">
                  <h3>🚀 Register for the AI Workshop - Just ₹97!</h3>
                  <button
                    className="close-button"
                    onClick={() => setIsFormModalOpen(false)}
                    aria-label="Close registration form">
                    <FiX />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="registration-form">
                  <div className="form-group">
                    <label htmlFor="name">
                      <FiUser className="input-icon" />
                      Full Name *
                    </label>
                    <input
                      ref={firstInputRef}
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter your full name"
                      className={formErrors.name ? 'error' : ''}
                      aria-describedby={formErrors.name ? 'name-error' : undefined}
                    />
                    {formErrors.name && (
                      <span id="name-error" className="error-message" role="alert">
                        {formErrors.name}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">
                      <FiMail className="input-icon" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter your email address"
                      className={formErrors.email ? 'error' : ''}
                      aria-describedby={formErrors.email ? 'email-error' : undefined}
                    />
                    {formErrors.email && (
                      <span id="email-error" className="error-message" role="alert">
                        {formErrors.email}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">
                      <FiPhone className="input-icon" />
                      Phone Number *
                    </label>
                    <div className="phone-input-group">
                      <SearchableCountryCodeSelect
                        options={countryCodes}
                        value={formData.countryCode}
                        onChange={(code) => handleInputChange('countryCode', code)}
                      />
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) =>
                          handleInputChange('phone', e.target.value.replace(/\D/g, ''))
                        }
                        placeholder="Enter your phone number"
                        className={formErrors.phone ? 'error' : ''}
                        aria-describedby={formErrors.phone ? 'phone-error' : undefined}
                        maxLength="15"
                      />
                    </div>
                    {formErrors.phone && (
                      <span id="phone-error" className="error-message" role="alert">
                        {formErrors.phone}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="profession">
                      <FiBriefcase className="input-icon" />
                      Profession/Role *
                    </label>
                    <select
                      id="profession"
                      name="profession"
                      value={formData.profession}
                      onChange={(e) => handleInputChange('profession', e.target.value)}
                      className={formErrors.profession ? 'error' : ''}
                      aria-describedby={
                        formErrors.profession ? 'profession-error' : undefined
                      }
                    >
                      <option value="">Select your profession</option>
                      <option value="Coach / Consultant">Coach / Consultant</option>
                      <option value="Healer / Therapist">Healer / Therapist</option>
                      <option value="Business Owner">Business Owner</option>
                      <option value="Entrepreneur">Entrepreneur</option>
                      <option value="Working Professional">Working Professional</option>
                      <option value="Freelancer">Freelancer</option>
                      <option value="Sales Professional">Sales Professional</option>
                      <option value="Marketer">Marketer</option>
                      <option value="Student">Student</option>
                      <option value="Other">Other</option>
                    </select>
                    {formErrors.profession && (
                      <span id="profession-error" className="error-message" role="alert">
                        {formErrors.profession}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.consent}
                        onChange={(e) => handleInputChange('consent', e.target.checked)}
                        className={formErrors.consent ? 'error' : ''}
                        aria-describedby={
                          formErrors.consent ? 'consent-error' : undefined
                        }
                      />
                      <span className="checkmark"></span>
                      I agree to receive updates about the program and understand the
                      terms & conditions *
                    </label>
                    {formErrors.consent && (
                      <span id="consent-error" className="error-message" role="alert">
                        {formErrors.consent}
                      </span>
                    )}
                  </div>

                  <button type="submit" className="submit-button" disabled={isSubmitting}>
                    {isSubmitting ? 'Processing...' : 'Register Now for ₹97 🚀'}
                  </button>

                  <p className="form-note">
                    After registration, you'll be redirected to the secure payment page.
                  </p>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Video Modal */}
        <AnimatePresence>
          {selectedVideo && (
            <motion.div
              className="video-modal-overlay"
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              exit={{opacity: 0}}
              onClick={(e) => {
                if (e.target === e.currentTarget) setSelectedVideo(null);
              }}
            >
              <motion.div
                className="video-modal-content"
                initial={{opacity: 0, scale: 0.8}}
                animate={{opacity: 1, scale: 1}}
                exit={{opacity: 0, scale: 0.8}}
                transition={{type: "spring", stiffness: 300, damping: 25}}
              >
                <div className="video-modal-header">
                  <h3>{selectedVideo.title}</h3>
                  <button
                    className="close-button"
                    onClick={() => setSelectedVideo(null)}
                    aria-label="Close video">
                    <FiX />
                  </button>
                </div>
                <div className="video-embed-container">
                  <iframe
                    src={`https://www.youtube.com/embed/${selectedVideo.embedId}?autoplay=1&rel=0`}
                    title={selectedVideo.title}
                    allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
                <div className="video-fallback">
                  <p>
                    Can't view the video?{' '}
                    <a
                      href={selectedVideo.url}
                      target="_blank"
                      rel="noopener noreferrer">
                      Open on YouTube
                    </a>
                  </p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sticky Footer */}
        <motion.div
          className="sticky-footer"
          initial={{y: "100%"}}
          animate={{y: "0%"}}
          transition={{duration: 0.7, ease: "easeInOut", delay: 1.5}}
        >
          <motion.div
            className="footer-info-box"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.5, delay: 2.2}}>
            <div className="date">Next Workshop:</div>
            <div className="time">{workshopDetails.formattedDate}</div>
          </motion.div>

          <motion.button
            className="footer-cta-button"
            onClick={() => {
              handleManualModalOpen();
              if (typeof fbq !== 'undefined') {
                fbq('track', 'InitiateCheckout', {
                  content_name: 'Sales Automation Workshop - Sticky Footer CTA',
                  content_category: 'Workshop',
                  value: 97,
                  currency: 'INR'
                });
              }
            }}
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
            animate={{
              scale: [1, 1.03, 1],
              transition: {
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2.5
              }
            }}
          >
            Register Now (₹97) 🚀
          </motion.button>
        </motion.div>

        <footer className="footer">
          <div className="container">
            <div className="footer-content">
              <p>&copy; 2025 10xAI Founder. All rights reserved.</p>
              <p style={{fontSize: '0.8rem', opacity: '0.7', marginTop: '1rem'}}>
                This site is not part of the Facebook website or Facebook Inc. Additionally, this site is NOT endorsed by. FACEBOOK is a trademark of FACEBOOK, Inc.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export default App;